/**
 * useScanShelve — mode « scan = mise en rayon ».
 *
 * Armé volontairement (coupé par défaut) : chaque code-barres scanné descend
 * 1 unité du lot FEFO de la réserve vers le rayon. Scanner 3 paquets range
 * 3 paquets. Sur la page des alertes de réapprovisionnement, l'alerte disparaît
 * ensuite d'elle-même : `reconcilePending` recalcule le stock à l'affichage.
 *
 * Choix structurants :
 * - Produits AU POIDS refusés. « 1 scan = 1 unité » n'a pas de sens quand la
 *   réserve est en kg : le vrac se range au seau ou au poids saisi, depuis
 *   l'onglet Mise en rayon. Détection en OU (jamais `??` : `is_weighted` vaut
 *   false par défaut en base et court-circuiterait le test sur l'unité).
 * - Lot choisi en FEFO (DLC la plus proche, sans DLC en dernier, puis réception
 *   la plus ancienne) — même ordre que la vente, donc rayon et caisse écoulent
 *   les lots de la même façon.
 * - Les scans rapides passent par une FILE sérialisée : un seul appel en vol.
 *   Sans elle, deux scans coup sur coup liraient le même stock local et
 *   viseraient le même lot. Le stock local est décrémenté de façon optimiste
 *   entre deux scans, puis resynchronisé avec le serveur en fin de salve.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { posApi } from '../api/pos.api';
import { WEDGE_SCANNER_STORAGE_KEY, isWedgeScannerEnabled } from './useBarcodeScan';
import { BackroomProduct } from '../types/lots.types';
import { playBeep } from '../utils/sound';

export interface ScanShelveFeedback {
  key: string;
  ok: boolean;
  productName: string;
  message: string;
}

/**
 * Fiche du dernier article mis en rayon, affichée en clair sous la barre.
 *
 * Le bandeau de retour disparaît en 2,2 s : il dit que le scan a marché, pas ce
 * qu'on vient de manipuler. Or à ce moment précis on a l'article en main et on
 * veut vérifier son prix ou ce qu'il en reste — jusqu'ici il fallait le retrouver
 * dans la liste ou ouvrir un second écran (signalé le 09/09/2026). La fiche reste
 * donc affichée jusqu'au scan suivant.
 */
export interface ScanShelveCard {
  key: string;
  productId: string;
  name: string;
  imageUrl?: string | null;
  categoryPath?: string | null;
  unit?: string | null;
  /** Prix de vente COURANT (caisse), pas le prix figé sur le lot à la réception. */
  priceCents?: number | null;
  /** Après le transfert, tous lots confondus. */
  display: number;
  backroom: number;
  lotNumber?: string | null;
  expiry?: string | null;
  /** Rayon plein connu pour ce produit (unité de vente), et d'où vient le chiffre. */
  shelfCapacity?: number | null;
  shelfCapacitySource?: string | null;
}

export interface ScanShelveLogEntry {
  key: string;
  at: number;
  productName: string;
  /** Renseigné seulement sur un transfert réussi — sert au « Annuler le dernier ». */
  lotId?: string;
  ok: boolean;
  message: string;
}

interface UseScanShelveOptions {
  /** Appelé après chaque salve de scans (et après une annulation) — pour rafraîchir la page hôte. */
  onShelved?: () => void;
}

const receptionTime = (dateStr?: string | null) =>
  dateStr ? new Date(dateStr).getTime() : 0;

/**
 * Ce qu'il y a VRAIMENT en rayon pour ce produit.
 *
 * `total_display` (tous lots) d'abord, `stock_quantity` de la fiche en repli —
 * les deux valent `SUM(stock_display)` des lots ACTIVE, le second étant servi
 * par les API antérieures au 09/09/2026. NE PAS additionner `pg.lots` : la
 * réserve ne ramène que les lots qui en ont encore, or le rayon est le plus
 * souvent tenu par un lot antérieur déjà vidé — d'où « 0 en rayon » sur du lait
 * qui en a 25, et un « rayon plein » noté à 1.
 */
const rayonReel = (pg?: BackroomProduct): number | undefined =>
  pg ? pg.total_display ?? pg.product.stock_quantity ?? undefined : undefined;

/**
 * Retour haptique — en PWA sur téléphone, dans les rayons, il porte plus que le bip
 * (sonnerie coupée, bruit ambiant). Ignoré par Safari iOS, qui n'expose pas vibrate :
 * le bip et le message à l'écran restent les canaux garantis.
 */
const vibrate = (pattern: number | number[]) => {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* pas de haptique sur cette plateforme */
  }
};

export type UseScanShelve = ReturnType<typeof useScanShelve>;

export function useScanShelve({ onShelved }: UseScanShelveOptions = {}) {
  const [armed, setArmed] = useState(false);
  /**
   * Douchette branchée sur un appareil TACTILE.
   *
   * Sur iPhone et iPad, l'écoute clavier est coupée par défaut — sans quoi le
   * clavier virtuel, qui saisit par salves, passerait pour un scanner. Une
   * douchette réelle doit donc être déclarée. Le réglage existait, mais son
   * interrupteur ne vivait que sur la page Réception guidée : depuis les
   * alertes de réapprovisionnement, scanner ne produisait rien du tout, sans
   * message (signalé le 26/08/2026). Il appartient au mode de scan, pas à une
   * page — il est donc porté ici, et l'interrupteur suit le bandeau.
   */
  const [wedgeOnTouch, setWedgeOnTouch] = useState(isWedgeScannerEnabled);

  const toggleWedge = useCallback(() => {
    setWedgeOnTouch(prev => {
      const next = !prev;
      try {
        if (next) localStorage.setItem(WEDGE_SCANNER_STORAGE_KEY, '1');
        else localStorage.removeItem(WEDGE_SCANNER_STORAGE_KEY);
      } catch {
        /* stockage indisponible : le réglage ne vaudra que pour cette session */
      }
      return next;
    });
  }, []);
  const [loadingStock, setLoadingStock] = useState(false);
  const [log, setLog] = useState<ScanShelveLogEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [undoing, setUndoing] = useState(false);
  /**
   * Dernier résultat, affiché en gros à l'écran puis effacé tout seul. Le bip dit
   * qu'il s'est passé quelque chose ; ce message dit QUOI — indispensable quand on
   * enchaîne les scans sans regarder l'écran, pour rattraper le mauvais article.
   */
  const [feedback, setFeedback] = useState<ScanShelveFeedback | null>(null);
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Dernier article mis en rayon — reste affiché jusqu'au scan suivant. */
  const [lastCard, setLastCard] = useState<ScanShelveCard | null>(null);
  const [savingCapacity, setSavingCapacity] = useState(false);

  const queueRef = useRef<string[]>([]);
  const busyRef = useRef(false);
  const seqRef = useRef(0);
  /** Lots en réserve — un ref, car la boucle async y lirait un état React périmé. */
  const stockRef = useRef<BackroomProduct[]>([]);
  /** Chargement en vol : la file l'attend, sinon un scan immédiat verrait une réserve vide. */
  const stockPromiseRef = useRef<Promise<void> | null>(null);
  /** Distingue « produit absent de la réserve » de « réserve pas chargée » (réseau HS). */
  const stockFailedRef = useRef(false);

  const onShelvedRef = useRef(onShelved);
  onShelvedRef.current = onShelved;

  // Point de sortie UNIQUE de tout résultat de scan : journal, message à l'écran,
  // bip et vibration. Passer par ici garantit qu'aucun cas (refus poids, code
  // inconnu, erreur réseau) ne signale à moitié.
  const pushLog = useCallback((entry: Omit<ScanShelveLogEntry, 'key' | 'at'>) => {
    // Compteur dédié pour la clé React : deux scans dans la même milliseconde
    // collideraient sur un Date.now(), et la liste est plafonnée (longueur constante).
    const key = `scan-${++seqRef.current}`;
    setLog(prev => [{ ...entry, key, at: Date.now() }, ...prev].slice(0, 30));

    setFeedback({ key, ok: entry.ok, productName: entry.productName, message: entry.message });

    // Succès : bip aigu + vibration brève. Refus : bip grave + double vibration,
    // nettement distincts sans avoir à lire.
    if (entry.ok) {
      playBeep();
      vibrate(60);
    } else {
      playBeep(300, 220);
      vibrate([90, 70, 90]);
    }

    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    // Un refus reste plus longtemps : il y a un motif à lire et un geste à corriger.
    feedbackTimerRef.current = setTimeout(
      () => setFeedback(null),
      entry.ok ? 2200 : 5000,
    );
  }, []);

  // Ne pas laisser un timer écrire dans un composant démonté (sortie de page en plein scan)
  useEffect(
    () => () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    },
    [],
  );

  const loadStock = useCallback(async () => {
    const run = (async () => {
      try {
        setLoadingStock(true);
        stockRef.current = await posApi.getBackroomLots();
        stockFailedRef.current = false;
      } catch (err) {
        console.error('[useScanShelve] chargement réserve:', err);
        stockRef.current = [];
        stockFailedRef.current = true;
      } finally {
        setLoadingStock(false);
      }
    })();
    stockPromiseRef.current = run;
    await run;
  }, []);

  /** Répercute un transfert localement pour que le scan suivant vise le bon lot. */
  const applyLocal = (lotId: string, qty: number) => {
    stockRef.current = stockRef.current.map(pg => ({
      ...pg,
      total_backroom: pg.lots.some(l => l.id === lotId)
        ? pg.total_backroom - qty
        : pg.total_backroom,
      // Le rayon vient du serveur, tous lots confondus : le suivre ici aussi,
      // sinon la fiche annoncerait l'état d'avant le geste. Les deux lectures sont
      // avancées ensemble, `stock_quantity` servant de repli à `total_display`.
      total_display: pg.lots.some(l => l.id === lotId)
        ? (pg.total_display ?? pg.product.stock_quantity ?? 0) + qty
        : pg.total_display,
      product: pg.lots.some(l => l.id === lotId)
        ? { ...pg.product, stock_quantity: (pg.product.stock_quantity ?? 0) + qty }
        : pg.product,
      lots: pg.lots.map(l =>
        l.id === lotId
          ? { ...l, stock_backroom: l.stock_backroom - qty, stock_display: l.stock_display + qty }
          : l,
      ),
    }));
  };

  /**
   * Recale la fiche sur ce que dit le serveur, une fois la salve retombée.
   *
   * Entre deux scans les chiffres sont OPTIMISTES (+1 posé localement). Ils
   * peuvent avoir dérivé entre-temps — une vente à la caisse, une mise en rayon
   * faite depuis un autre écran. Sans ce recalage, la fiche resterait figée sur
   * l'estimation du geste jusqu'au scan suivant : le premier reproche fait au
   * lendemain de la mise en service (09/09/2026).
   *
   * Produit sorti de la liste = plus rien en réserve : on le dit, sans toucher
   * au rayon, dont le serveur ne parle plus.
   */
  const refreshCardFromStock = useCallback(() => {
    setLastCard(prev => {
      if (!prev) return prev;
      const pg = stockRef.current.find(p => p.product.id === prev.productId);
      if (!pg) return { ...prev, backroom: 0 };
      return {
        ...prev,
        display: rayonReel(pg) ?? prev.display,
        backroom: pg.lots.reduce((n, l) => n + (l.stock_backroom || 0), 0),
        shelfCapacity: pg.product.shelf_capacity ?? null,
        shelfCapacitySource: pg.product.shelf_capacity_source ?? null,
      };
    });
  }, []);

  const shelveOne = async (barcode: string) => {
    if (stockFailedRef.current) {
      pushLog({
        ok: false,
        productName: barcode,
        message: 'Réserve non chargée — coupe et réactive le mode',
      });
      return;
    }

    const pg = stockRef.current.find(
      p => p.product.barcode === barcode || p.product.code === barcode,
    );

    if (!pg) {
      pushLog({ ok: false, productName: barcode, message: 'Rien en réserve pour ce code' });
      return;
    }

    const isWeight = !!(
      pg.product.is_weighted ||
      pg.product.unit === 'kg' ||
      pg.lots.some(l => l.is_weighted || l.unit === 'kg')
    );
    if (isWeight) {
      pushLog({
        ok: false,
        productName: pg.product.name,
        message: 'Au poids — à ranger depuis Mise en rayon',
      });
      return;
    }

    const lot = pg.lots
      .filter(l => l.stock_backroom > 0)
      .sort((a, b) => {
        const ea = a.expiry_date ? new Date(a.expiry_date).getTime() : Infinity;
        const eb = b.expiry_date ? new Date(b.expiry_date).getTime() : Infinity;
        if (ea !== eb) return ea - eb;
        return receptionTime(a.reception_date) - receptionTime(b.reception_date);
      })[0];

    if (!lot) {
      pushLog({ ok: false, productName: pg.product.name, message: 'Plus rien en réserve' });
      return;
    }

    try {
      await posApi.moveToDisplay(lot.id, 1);
      applyLocal(lot.id, 1);

      // Relire le groupe APRÈS le transfert : les totaux affichés doivent être ceux
      // d'après le geste, sinon la fiche annonce ce qu'on vient de corriger.
      const apres = stockRef.current.find(p => p.product.id === pg.product.id);
      const lots = apres?.lots ?? [];
      setLastCard({
        key: `card-${seqRef.current + 1}`,
        productId: pg.product.id,
        name: pg.product.name,
        imageUrl: pg.product.image_url ?? null,
        categoryPath: pg.product.category?.full_path ?? pg.product.category?.label ?? null,
        unit: pg.product.unit ?? null,
        priceCents: pg.product.current_price_ttc_cents ?? null,
        // Rayon RÉEL, tous lots confondus (`total_display` du serveur). Additionner
        // `lots` ne compterait que les lots ayant encore de la réserve — donc 0 pour
        // un rayon tenu par un lot antérieur déjà vidé (Lait des Ardennes : 25 en
        // rayon, 0 affiché). Le chiffre sert aussi de « rayon plein » au bouton :
        // faux, il serait ENCODÉ faux (constaté le 09/09/2026).
        display: rayonReel(apres) ?? lots.reduce((n, l) => n + (l.stock_display || 0), 0),
        backroom: lots.reduce((n, l) => n + (l.stock_backroom || 0), 0),
        lotNumber: lot.lot_number ?? null,
        expiry: lot.expiry_date ?? null,
        shelfCapacity: pg.product.shelf_capacity ?? null,
        shelfCapacitySource: pg.product.shelf_capacity_source ?? null,
      });

      pushLog({ ok: true, productName: pg.product.name, lotId: lot.id, message: '+1 en rayon' });
    } catch (err) {
      pushLog({
        ok: false,
        productName: pg.product.name,
        message: err instanceof Error ? err.message : 'Échec du transfert',
      });
    }
  };

  const drainQueue = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      // Un scan peut arriver avant la fin du chargement initial de la réserve :
      // sans cette attente, il conclurait à tort « rien en réserve ».
      if (stockPromiseRef.current) await stockPromiseRef.current;
      while (queueRef.current.length > 0) {
        await shelveOne(queueRef.current.shift()!);
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
      // L'état local n'était qu'optimiste : recaler sur le serveur, et laisser la
      // page hôte se rafraîchir (les alertes traitées disparaissent d'elles-mêmes).
      await loadStock();
      refreshCardFromStock();
      onShelvedRef.current?.();
    }
  }, [loadStock, refreshCardFromStock]);

  const handleScan = useCallback(
    (barcode: string) => {
      queueRef.current.push(barcode);
      void drainQueue();
    },
    [drainQueue],
  );

  /** Arme le mode et charge la réserve (pas de requête tant que le mode est coupé). */
  const arm = useCallback(async () => {
    setArmed(true);
    await loadStock();
  }, [loadStock]);

  const disarm = useCallback(() => {
    setArmed(false);
    queueRef.current = [];
    setLog([]);
    setFeedback(null);
    setLastCard(null);
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
  }, []);

  /**
   * Noter « rayon plein » pour l'article qu'on vient de ranger.
   *
   * C'est l'information que seul le geste connaît : on scanne jusqu'à ce que
   * l'étal soit plein, et à cet instant le rayon COMPTE le rayon plein. La saisie
   * prime ensuite sur la déduction nocturne (`shelf_capacity_source = 'manual'`)
   * et alimente le « Ajouter N » de la page des alertes — qui, sans capacité,
   * propose de descendre toute la réserve.
   *
   * `null` efface la valeur et rend la main à l'observation.
   */
  const setShelfFull = useCallback(
    async (capacity: number | null) => {
      const card = lastCard;
      if (!card) return;
      if (capacity != null && (!Number.isFinite(capacity) || capacity < 0)) return;
      try {
        setSavingCapacity(true);
        await posApi.setShelfCapacity(card.productId, capacity);
        const source = capacity == null ? null : 'manual';
        // Répercuter dans la réserve en mémoire : sans ça, un nouveau scan du même
        // article réafficherait la capacité d'avant (le stock local n'est rechargé
        // qu'en fin de salve).
        stockRef.current = stockRef.current.map(pg =>
          pg.product.id === card.productId
            ? {
                ...pg,
                product: {
                  ...pg.product,
                  shelf_capacity: capacity,
                  shelf_capacity_source: source,
                },
              }
            : pg,
        );
        setLastCard(prev =>
          prev && prev.productId === card.productId
            ? { ...prev, shelfCapacity: capacity, shelfCapacitySource: source }
            : prev,
        );
        playBeep(700, 120);
        vibrate(40);
        // La page hôte recalcule « Ajouter N » avec la nouvelle capacité.
        onShelvedRef.current?.();
      } catch (err) {
        alert(err instanceof Error ? err.message : "Échec de l'enregistrement du rayon plein");
      } finally {
        setSavingCapacity(false);
      }
    },
    [lastCard],
  );

  /** Annule le dernier scan réussi : remonte 1 unité en réserve sur LE MÊME lot. */
  const undoLast = useCallback(async () => {
    const last = log.find(e => e.ok && e.lotId);
    if (!last?.lotId) return;
    try {
      setUndoing(true);
      await posApi.moveToBackroom(last.lotId, 1);
      applyLocal(last.lotId, -1);
      setLog(prev => prev.filter(e => e.key !== last.key));
      playBeep(600, 120);
      vibrate(40);
      // L'annulation mérite sa confirmation à l'écran comme un scan : sur téléphone,
      // le bouton peut être hors du champ de vision au moment du tap.
      setFeedback({
        key: `undo-${++seqRef.current}`,
        ok: true,
        productName: last.productName,
        message: '↩︎ Remis en réserve',
      });
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = setTimeout(() => setFeedback(null), 2200);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Échec de l'annulation");
    } finally {
      setUndoing(false);
      await loadStock();
      refreshCardFromStock();
      onShelvedRef.current?.();
    }
  }, [log, loadStock, refreshCardFromStock]);

  return {
    armed,
    arm,
    disarm,
    wedgeOnTouch,
    toggleWedge,
    loadingStock,
    feedback,
    lastCard,
    setShelfFull,
    savingCapacity,
    log,
    busy,
    undoing,
    shelvedCount: log.filter(e => e.ok).length,
    canUndo: log.some(e => e.ok && e.lotId),
    handleScan,
    undoLast,
  };
}
